/**
 * Obtención de la tasa de cambio USD↔MXN en vivo.
 *
 * Orden de preferencia:
 *   1. Override manual del usuario (Configuración) — si existe, no se pega a la red.
 *   2. Cache fresco (< CACHE_TTL_MS) en localStorage.
 *   3. open.er-api.com (gratis, sin API key, CORS ✓).
 *   4. frankfurter.app (fallback, gratis, CORS ✓).
 *   5. Fallback hardcodeado (FALLBACK_RATES) — nunca rompe.
 *
 * Siempre actualiza las tasas en memoria de `currency.js` vía setRates().
 */

import { setRates, FALLBACK_RATES } from '../utils/currency.js';
import {
  getCachedRates,
  setCachedRates,
  getRateOverride,
} from '../currencyStore.js';

const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12 h

/**
 * Resuelve las tasas y las deja activas en memoria.
 * @returns {Promise<{rates: object, ts: number, source: string}>}
 */
export async function fetchRates() {
  // 1. Override manual: gana sobre todo.
  const override = getRateOverride();
  if (override) {
    const payload = { rates: { USD: 1, MXN: override }, ts: Date.now(), source: 'manual' };
    setRates(payload.rates);
    return payload;
  }

  // 2. Cache fresco.
  const cached = getCachedRates();
  if (cached && cached.ts && Date.now() - cached.ts < CACHE_TTL_MS) {
    setRates(cached.rates);
    return cached;
  }

  // 3 + 4. Fuentes en vivo.
  const sources = [fetchFromErApi, fetchFromFrankfurter];
  for (const src of sources) {
    try {
      const payload = await src();
      if (payload && payload.rates && payload.rates.MXN) {
        setRates(payload.rates);
        setCachedRates(payload);
        return payload;
      }
    } catch (e) {
      console.warn('Fuente de tasa de cambio falló:', e && e.message);
    }
  }

  // 5. Fallback: usa cache viejo si lo hay, si no el hardcodeado.
  if (cached && cached.rates) {
    setRates(cached.rates);
    return cached;
  }
  const fallback = { rates: { ...FALLBACK_RATES }, ts: Date.now(), source: 'fallback' };
  setRates(fallback.rates);
  return fallback;
}

async function fetchFromErApi() {
  const res = await fetch('https://open.er-api.com/v6/latest/USD', { credentials: 'omit' });
  if (!res.ok) throw new Error('er-api HTTP ' + res.status);
  const data = await res.json();
  if (!data || data.result !== 'success' || !data.rates) throw new Error('er-api respuesta inválida');
  // ts a partir de la fecha de actualización oficial si viene, si no ahora.
  const ts = data.time_last_update_unix ? data.time_last_update_unix * 1000 : Date.now();
  return { rates: data.rates, ts, source: 'open.er-api.com' };
}

async function fetchFromFrankfurter() {
  const res = await fetch('https://api.frankfurter.app/latest?from=USD&to=MXN', { credentials: 'omit' });
  if (!res.ok) throw new Error('frankfurter HTTP ' + res.status);
  const data = await res.json();
  if (!data || !data.rates || !data.rates.MXN) throw new Error('frankfurter respuesta inválida');
  const ts = data.date ? new Date(data.date + 'T00:00:00Z').getTime() : Date.now();
  return { rates: { USD: 1, MXN: data.rates.MXN }, ts, source: 'frankfurter.app' };
}
